import EngineCleanupBase from '../../../../services/engines/EngineCleanupBase';
import ComponentNotifier from '../../../../services/wss/ComponentNotifier';
import prioUpdater from '../../../../components/shared/PrioUpdater';
import entryUpdater from '../../../DashboardComponents/TemplateEngine/InputFormRenderer/EntryUpdater';
import userStateEngine2 from '../../../../controllers/UserState/UserStateEngine2';
import { UserStateKeys } from '../../../../controllers/UserState/UserStateKeys';
import AppContext from '../../../../controllers/AppContext';
import { TabNames } from '../../../../services/wss/events/TabNames';

const _name = 'NoteOptionsPanelEngine';
const NEW_NOTE_TABS = [TabNames.YourNotes, TabNames.YourData, TabNames.Library];

const _defaultState = () => ({
    entry: null,
    viewMode: 'md',
    bgMode: 'dark',
    inFocusMode: false,
    isEditing: false,
    draftBody: '',
    deleteOpen: false,
    newNoteOpen: false,
    newNotePos: null,
});

/**
 * NoteOptionsPanelEngine
 * Owns all business logic for the NoteOptionsPanel, scoped per panelId.
 * State is stored per panel in _panels Map — no shared singleton state.
 * Components pass panelId to all engine calls and to useNoteOptionsPanel(panelId).
 *
 * FIXED — every method that builds a save payload (setPinned, setEntryState, setViewMode,
 * confirmDelete, startEditing, saveBody, handleEmail) now takes the live `entry` as a
 * parameter instead of reading it from the internal `_panels` Map. That internal copy
 * (p.entry) was only ever refreshed via setEntry (on rk change) or OnJournalUpdated (on a
 * WSS round-trip) — both lag behind saves made through other paths (e.g. JInspector), so
 * building a save payload from p.entry risked silently reverting fields that changed
 * elsewhere in between (e.g. remark disappearing after a Pin toggle). The caller
 * (ContentPanelHeader / NoteOptionsPanelToggles / NoteOptionsPanelActions / ContentPanelInner)
 * already receives a fresh `entry` prop from the owning per-tab engine (YourDataEngine,
 * GroupNodeEngine, FloatingPanelEngine) — same pattern ContentPanelMeta.handleSave already
 * used correctly for JInspector saves. p.entry is still kept in sync (best-effort cache for
 * getEntry()) but is no longer the source of truth for any save.
 */
class NoteOptionsPanelEngine extends EngineCleanupBase {
    _panels = new Map(); // panelId → state object

    bootup = () => {
        this.initialize(_name);
    };

    // ─── panel state ──────────────────────────────────────

    _get(panelId) {
        if (!this._panels.has(panelId)) {
            this._panels.set(panelId, _defaultState());
        }
        return this._panels.get(panelId);
    }

    getState(panelId = 'default') {
        return this._get(panelId);
    }

    // ─── lifecycle ────────────────────────────────────────

    OnJournalUpdated = (entries) => {
        try {
            if (!Array.isArray(entries) || !entries.length) return false;
            let updated = false;

            for (const [panelId, p] of this._panels) {
                entries.forEach((entry) => {
                    if (entry.rk !== p.entry?.rk) {
                        return;
                    }

                    updated = true;

                    // set panelId entry to received data
                    p.entry = { ...entry };
                    this._notify(panelId);
                });
            }
            return updated;
        } catch (error) {
            this.logError('OnJournalUpdated', error.message);
            return false;
        }
    };

    // ─── entry ────────────────────────────────────────────

    setEntry = (entry, isTemplate, panelId = 'default') => {
        const p = this._get(panelId);
        p.entry = entry;
        p.viewMode = isTemplate ? 'template' : entry?.viewMode || (entry?.format === 'html' ? 'html' : 'md');
        p.isEditing = false;
        p.draftBody = '';
        this._notify(panelId);
    };

    getEntry = (panelId = 'default') => this._get(panelId).entry;

    // ─── focus ────────────────────────────────────────────

    enterFocus = (panelId = 'default') => {
        this._get(panelId).inFocusMode = true;
        userStateEngine2.set([{ name: UserStateKeys.contentFocusMode, value: 'true' }]);
        this._notify(panelId);
    };

    exitFocus = (panelId = 'default') => {
        this._get(panelId).inFocusMode = false;
        userStateEngine2.set([{ name: UserStateKeys.contentFocusMode, value: 'false' }]);
        this._notify(panelId);
    };

    toggleFocus = (panelId = 'default') => {
        if (this._get(panelId).inFocusMode) this.exitFocus(panelId);
        else this.enterFocus(panelId);
    };

    /** @returns {boolean} true if any panel is currently in focus mode */
    isAnyInFocusMode = () => {
        for (const p of this._panels.values()) {
            if (p.inFocusMode) return true;
        }
        return false;
    };

    // ─── view / bg mode ───────────────────────────────────

    /**
     * @param {object} entry - live entry (caller's fresh prop, not the internal cache)
     * @param {string} newMode
     * @param {string} [panelId='default']
     */
    setViewMode = async (entry, newMode, panelId = 'default') => {
        const p = this._get(panelId);
        p.viewMode = newMode;
        p.entry = { ...entry, viewMode: newMode };
        this._notify(panelId);
        await prioUpdater.save({ ...entry, viewMode: newMode }, null);
    };

    setBgMode = (newMode, panelId = 'default') => {
        this._get(panelId).bgMode = newMode;
        this._notify(panelId);
    };

    // ─── state / pinned ───────────────────────────────────

    /**
     * @param {object} entry - live entry (caller's fresh prop, not the internal cache)
     * @param {number} newStateId
     * @param {string} [panelId='default']
     */
    setEntryState = async (entry, newStateId, panelId = 'default') => {
        if (!entry || newStateId === entry.stateId) return;
        this._get(panelId).entry = { ...entry, stateId: newStateId };
        await prioUpdater.save({ ...entry, stateId: newStateId }, null);
    };

    /**
     * @param {object} entry - live entry (caller's fresh prop, not the internal cache)
     * @param {boolean} isPinned
     * @param {string} [panelId='default']
     */
    setPinned = async (entry, isPinned, panelId = 'default') => {
        if (!entry || isPinned === entry.isPinned) {
            return;
        }

        this._get(panelId).entry = { ...entry, isPinned };
        await prioUpdater.save({ ...entry, isPinned }, null);
    };

    // ─── edit body ────────────────────────────────────────

    /**
     * @param {object} entry - live entry (caller's fresh prop, not the internal cache)
     * @param {string} [panelId='default']
     */
    startEditing = (entry, panelId = 'default') => {
        try {
            if (!entry) return;
            const p = this._get(panelId);
            p.entry = entry;
            p.draftBody = entry.body ?? '';
            p.isEditing = true;
            this._notify(panelId);
        } catch (error) {
            this.logError('startEditing', error.message);
        }
    };

    /**
     * @param {object} entry - live entry (caller's fresh prop, not the internal cache)
     * @param {string} [panelId='default']
     */
    saveBody = async (entry, panelId = 'default') => {
        const p = this._get(panelId);
        const result = await entryUpdater.savePlainTextBody(entry, p.draftBody);
        if (!result) return;
        p.entry = { ...entry, body: p.draftBody };
        p.isEditing = false;
        p.draftBody = '';
        this._notify(panelId);
    };

    setDraftBody = (value, panelId = 'default') => {
        this._get(panelId).draftBody = value;
    };

    cancelEdit = (panelId = 'default') => {
        const p = this._get(panelId);
        p.isEditing = false;
        p.draftBody = '';
        this._notify(panelId);
    };

    getDraftBody = (panelId = 'default') => this._get(panelId).draftBody;

    // ─── delete ───────────────────────────────────────────

    openDelete = (panelId = 'default') => {
        this._get(panelId).deleteOpen = true;
        this._notify(panelId);
    };

    closeDelete = (panelId = 'default') => {
        this._get(panelId).deleteOpen = false;
        this._notify(panelId);
    };

    /**
     * @param {object} entry - live entry (caller's fresh prop, not the internal cache)
     * @param {string} [panelId='default']
     */
    confirmDelete = async (entry, panelId = 'default') => {
        const p = this._get(panelId);
        p.deleteOpen = false;
        p.entry = { ...entry, stateId: 999 };
        await prioUpdater.save({ ...entry, stateId: 999 }, null);
        this._notify(panelId);
    };

    // ─── new note ─────────────────────────────────────────

    openNewNote = (e, panelId = 'default') => {
        const p = this._get(panelId);
        if (!p.entry) return;

        const dialogWidth = 320;
        const left = e.clientX + dialogWidth > window.innerWidth ? e.clientX - dialogWidth : e.clientX;

        p.newNotePos = { top: e.clientY, left };
        p.newNoteOpen = true;

        this._notify(panelId);
    };

    closeNewNote = (panelId = 'default') => {
        const p = this._get(panelId);
        p.newNoteOpen = false;
        p.newNotePos = null;
        this._notify(panelId);
    };

    // ─── share actions ────────────────────────────────────

    /**
     * @param {object} entry - live entry (caller's fresh prop, not the internal cache)
     * @param {string} [panelId='default']
     */
    handleEmail = (entry, panelId = 'default') => {
        if (!entry) return;
        const subject = encodeURIComponent(`[Jerny] ${entry.title}`);
        const body = encodeURIComponent(`Type: ${entry.type}\n\n${entry.body}`);
        window.open(`mailto:?subject=${subject}&body=${body}`);
    };

    handleDownload = (panelId = 'default') => {};
    handleShare = (panelId = 'default') => {};

    // ─── queries ──────────────────────────────────────────

    getViewMode = (panelId = 'default') => this._get(panelId).viewMode;
    getBgMode = (panelId = 'default') => this._get(panelId).bgMode;
    isInFocusMode = (panelId = 'default') => this._get(panelId).inFocusMode;
    getIsEditing = (panelId = 'default') => this._get(panelId).isEditing;
    isDeleteOpen = (panelId = 'default') => this._get(panelId).deleteOpen;
    isNewNoteOpen = (panelId = 'default') => this._get(panelId).newNoteOpen;
    getNewNotePos = (panelId = 'default') => this._get(panelId).newNotePos;

    showNewNote = () => {
        const tab = AppContext.getCurrentTab();
        if (!NEW_NOTE_TABS.includes(tab)) return false;
        if (tab === TabNames.YourData || tab === TabNames.Library) return true;
        return !!AppContext.getActiveGroup();
    };

    // ─── private ──────────────────────────────────────────

    _notify = (panelId) => {
        const p = this._get(panelId);
        ComponentNotifier.notify(_name, 'stateChanged', {
            panelId,
            entry: p.entry,
            viewMode: p.viewMode,
            bgMode: p.bgMode,
            inFocusMode: p.inFocusMode,
            isEditing: p.isEditing,
            draftBody: p.draftBody,
            deleteOpen: p.deleteOpen,
            newNoteOpen: p.newNoteOpen,
            newNotePos: p.newNotePos,
        });
    };
}

const noteOptionsPanelEngine = new NoteOptionsPanelEngine();
export default noteOptionsPanelEngine;
